/** Scope navigation responses to the current actor, workspace and project; last confirmed grants draw at once, unknown grants fail closed. */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../platform/auth/AuthContext';
import { WORKSPACE_NAVIGATION_CHANGED_EVENT, type WorkspaceNavigation } from '../services/WorkspaceNavigationService';
import { subscribeToNavigationChanges } from '../services/WorkspaceNavigationLiveUpdates';

import { cachedNavigation, clearNavigationCache, loadNavigation, navigationCacheKey, navigationIsFresh } from '../services/WorkspaceNavigationCache';
export function useWorkspaceNavigation(projectId?: string | null) {
  const { activeWorkspaceId, activeWorkspace, user, workspaceMemberships } = useAuth();
  const authoritative = !!activeWorkspaceId || workspaceMemberships.length > 0 || !!user?.workspaceId || !!user?.workspace_id;
  const key = navigationCacheKey(user?.id || '', activeWorkspaceId || '', projectId);
  const [result, setResult] = useState<{ key: string; navigation: WorkspaceNavigation | null; error: string } | null>(null);
  const [revision, setRevision] = useState(0);
  const checkedRevision = useRef(0);
  useEffect(() => {
    const refresh = () => { setRevision(value => value + 1); };
    // Membership, user-role and role-default changes do not all broadcast yet.
    // Revalidate on focus while retaining the visible rail until the reply arrives.
    const onFocus = refresh;
    const invalidate = () => { clearNavigationCache(); setResult(null); refresh(); };
    window.addEventListener(WORKSPACE_NAVIGATION_CHANGED_EVENT, invalidate);
    window.addEventListener('focus', onFocus);
    window.addEventListener('quantis:auth-cleared', invalidate);
    const unsubscribe = user?.id ? subscribeToNavigationChanges(user.id, invalidate, refresh) : undefined;
    return () => {
      unsubscribe?.();
      window.removeEventListener(WORKSPACE_NAVIGATION_CHANGED_EVENT, invalidate);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('quantis:auth-cleared', invalidate);
    };
  }, [user?.id, key]);
  useEffect(() => {
    let active = true;
    if (!activeWorkspaceId || !user?.id) return;
    const cached = cachedNavigation(key);
    // Older grants still draw (see `navigation` below) while this revalidates.
    if (checkedRevision.current === revision && cached && navigationIsFresh(key)) {
      // Another rail or prefetch can fill the cache after this hook rendered.
      // Publish it so the render that reported loading can settle without I/O.
      setResult({ key, navigation: cached, error: '' });
      return;
    }
    checkedRevision.current = revision;
    const request = loadNavigation(user.id, activeWorkspaceId, projectId);
    void request.then(navigation => {
      if (active && navigation.workspaceId === activeWorkspaceId && navigation.userId === user.id && (navigation.projectId || '') === (projectId || '')) setResult({ key, navigation, error: '' });
    }).catch(error => {
      if (active) setResult({ key, navigation: null, error: error instanceof Error ? error.message : 'Unable to load navigation' });
    });
    return () => { active = false; };
  }, [activeWorkspaceId, user?.id, projectId, key, revision]);
  const current = result?.key === key ? result : null;
  const navigation = current?.navigation || cachedNavigation(key);
  return { internalWorkspace: !!activeWorkspaceId && activeWorkspace?.id === activeWorkspaceId && activeWorkspace.type === 'internal', authoritative, navigation, loading: authoritative && !current && !navigation, error: current?.error || '' };
}
